/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/14/22
 */

import React from "react";
import ViewportEvent from "./ViewportEvent";
import { Employee, OverlapEvent } from "../interfaces/Global";
import { getEventType } from "./event-types";
import { Stack, Typography } from "@mui/material";
import { utcToZonedTime } from "date-fns-tz";
import { format } from "../dates-helper";

export interface OverlapViewportEventProps {
	viewportRange: Interval
	rangeResolution: number
	employee: Employee
	event: OverlapEvent
}

export default function OverlapViewportEvent(props: OverlapViewportEventProps) {

	return (
		<ViewportEvent
			viewportRange={props.viewportRange}
			rangeResolution={props.rangeResolution}
			normalizeTimezone={props.employee.timeZone}
			event={{
				title: "Events overlap",
				typeId: "OVERLAP",
				startDatetime: new Date(props.event.startDatetime),
				endDatetime: new Date(props.event.endDatetime)
			}}
			details={false}
			tooltip={
				<EventTooltip
					event={props.event}
					employee={props.employee}
				/>
			}
			sx={{
				height: 3,
				background: "repeating-linear-gradient(90deg,orange,orange 3px,transparent 3px,transparent 6px)",
				zIndex: 99
			}}
			viewportSx={{
				justifyContent: 'flex-end'
			}}
		/>
	);

}

interface EventTooltipProps {
	event: OverlapEvent
	employee: Employee
}

function EventTooltip({ event, employee }: EventTooltipProps) {

	const eStartDate = utcToZonedTime(event.startDatetime, employee.timeZone);
	const eEndDate = utcToZonedTime(event.endDatetime, employee.timeZone);

	return (
		<Stack>
			<div>{employee.displayName}</div>
			<Typography>Multiple Events</Typography>
			<Stack>From: {format(eStartDate, 'Pp')}</Stack>
			<Stack>To: {format(eEndDate, 'Pp')}</Stack>
			{event.events.map(e =>
				<React.Fragment key={e.id}>
					<Stack pt={2} />
					<Typography>{getEventType(e.typeId)?.name}</Typography>
					<Stack>From: {format(e.startDatetime, 'Pp')}</Stack>
					<Stack>To: {format(e.endDatetime, 'Pp')}</Stack>
				</React.Fragment>)}
		</Stack>
	)

}